import React, { useState } from 'react';
import Dialog from "@shared/ui/molecules/Dialog";
import DialogHeader from '@shared/dialogs/DialogHeader';
import { useDispatch, useSelector } from "react-redux";
import { closeDialog } from '@shared/store/dialog.duck';
import {
  CarParkSpace,
  deleteCarParkingSpace,
  requestUpdateCarParkingSpace
} from '@features/whereabouts/services/CarParkingPlanService';
import TextField from "@shared/ui/atoms/TextField";
import { PrimaryButton } from "@shared/ui/atoms/buttons/PrimaryButton";
import {
  selectSelectedCarParkingPlan,
  setSelectedCarPark,
  setSelectedCarParkSpace
} from '@features/whereabouts/store/officeView.duck';
import { safeParseInt } from '@shared/utils/MathUtils';
import { Switch } from "@shared/ui/atoms/Switch";
import { IsSuperAdmin } from '@shared/hooks/IsSuperAdmin';
import styled from "styled-components";
import { OutlineButton } from "@shared/ui/atoms/buttons/OutlineButton";
import Colours from "@shared/ui/atoms/Colours";
import { BodyRegular } from "@shared/ui/atoms/fonts/Body";
import { useTranslation } from "react-i18next";
import { failureNotification } from '@shared/store/notification.duck';

export function EditCarParkingSpaceDialog(props: Props) {
  const {parkingSpace} = props.payload;
  const dispatch = useDispatch();
  const selectedCarParkPlanOnOfficeView = useSelector(selectSelectedCarParkingPlan);
  const isSuperAdmin = IsSuperAdmin();
  const {t} = useTranslation();

  const [label, setLabel] = useState(props?.payload?.parkingSpace?.label ?? '');
  const [x, setX] = useState(props?.payload?.parkingSpace?.x ?? 0);
  const [y, setY] = useState(props?.payload?.parkingSpace?.y ?? 0);
  const [width, setWidth] = useState(props?.payload?.parkingSpace?.width ?? 0);
  const [height, setHeight] = useState(props?.payload?.parkingSpace?.height ?? 0);
  const [rotation, setRotation] = useState(props?.payload?.parkingSpace?.rotation ?? 0);
  const [active, setActive] = useState(props?.payload?.parkingSpace?.active ?? false);
  const [loading, setLoading] = useState(false);
  const [deletionRequested, setDeletionRequested] = useState(false);

  const close = () => {
    dispatch(closeDialog());
  }

  const updateX = (val: string) => {
    try {
      const newX = safeParseInt(val);
      setX(newX);
      updateSpace(label, newX, y, width, height, rotation, active);
    } catch (e) {
      // safeParseInt throws on empty/non-numeric input — keep the previous value.
    }
  }

  const updateY = (val: string) => {
    try {
      const newY = safeParseInt(val);
      setY(newY);
      updateSpace(label, x, newY, width, height, rotation, active);
    } catch (e) {
      // safeParseInt throws on empty/non-numeric input — keep the previous value.
    }
  }

  const updateWidth = (val: string) => {
    try {
      const newWidth = safeParseInt(val);
      setWidth(newWidth);
      updateSpace(label, x, y, newWidth, height, rotation, active);
    } catch (e) {
      // safeParseInt throws on empty/non-numeric input — keep the previous value.
    }
  }

  const updateHeight = (val: string) => {
    try {
      const newHeight = safeParseInt(val);
      setHeight(newHeight);
      updateSpace(label, x, y, width, newHeight, rotation, active);
    } catch (e) {
      // safeParseInt throws on empty/non-numeric input — keep the previous value.
    }
  }

  const updateRotation = (val: string) => {
    try {
      const newRotation = safeParseInt(val);
      setRotation(newRotation);
      updateSpace(label, x, y, width, height, newRotation, active);
    } catch (e) {
      // safeParseInt throws on empty/non-numeric input — keep the previous value.
    }
  }

  const updateActive = (val: boolean) => {
    try {
      setActive(val);
      updateSpace(label, x, y, width, height, rotation, val);
    } catch (e) {
      // safeParseInt throws on empty/non-numeric input — keep the previous value.
    }
  }

  const updateSpace = (label: string, x: number, y: number, width: number, height: number, rotation: number, active: boolean) => {
    if (selectedCarParkPlanOnOfficeView) {
      const updatedCarParkSpace = {
        ...parkingSpace,
        label: label,
        width: width,
        height: height,
        x: x,
        y: y,
        rotation: rotation,
        active: active,
      }

      const updatedParkingSpaces = [
        ...selectedCarParkPlanOnOfficeView.parkingSpaces.filter(ps => ps.id !== parkingSpace.id),
        updatedCarParkSpace
      ];

      const updatedCarPark = {
        ...selectedCarParkPlanOnOfficeView,
        parkingSpaces: updatedParkingSpaces
      };

      if (selectedCarParkPlanOnOfficeView) {
        dispatch(setSelectedCarPark(updatedCarPark));
      }
    }
  }

  const save = async () => {
    setLoading(true);
    try {
      const updatedSpace = {
        ...props.payload.parkingSpace,
        x: x, y: y, width: width, height: height, rotation: rotation, label: label, active: active
      }
      const updatedCarParkSpace = await requestUpdateCarParkingSpace(updatedSpace);

      if (selectedCarParkPlanOnOfficeView) {
        const updatedParkingSpaces = [
          ...selectedCarParkPlanOnOfficeView.parkingSpaces.filter(ps => ps.id !== updatedCarParkSpace.id).filter(ps => !!ps.id),
          updatedCarParkSpace
        ];
        const updatedCarPark = {
          ...selectedCarParkPlanOnOfficeView,
          parkingSpaces: updatedParkingSpaces
        };

        if (selectedCarParkPlanOnOfficeView) {
          dispatch(setSelectedCarPark(updatedCarPark));
        }
      }
      dispatch(closeDialog());
      dispatch(setSelectedCarParkSpace(undefined));
    } finally {
      setLoading(false);
    }
  }

  const deleteSpace = async () => {
    setLoading(true);
    try {

      if (selectedCarParkPlanOnOfficeView) {
        await deleteCarParkingSpace(props.payload.parkingSpace);
        const updatedParkingSpaces = [
          ...selectedCarParkPlanOnOfficeView.parkingSpaces.filter(ps => ps.id !== props.payload.parkingSpace.id).filter(ps => !!ps.id),
        ];
        const updatedCarPark = {
          ...selectedCarParkPlanOnOfficeView,
          parkingSpaces: updatedParkingSpaces
        };

        if (selectedCarParkPlanOnOfficeView) {
          dispatch(setSelectedCarPark(updatedCarPark));
        }
      } else {
        throw new Error('No car park plan');
      }
      dispatch(closeDialog());
      dispatch(setSelectedCarParkSpace(undefined));
    } catch (e) {
      dispatch(failureNotification('Failed to delete'))
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog isOpen={true} onClose={() => close()} style={{marginLeft: '50%'}}>
      <DialogHeader title={'car-parking.edit-space-title'} />
      <Container>
        <TextField type={'text'} onChange={setLabel} value={label} label={"Label"} disabled={loading || deletionRequested} />
        {deletionRequested ? <>
          <BodyRegular>{t('car-parking.are-you-sure-delete')}</BodyRegular>

          <OutlineButton text={"button.yes-delete"} fullWidth={true} borderColour={Colours.red} disabled={loading} click={deleteSpace} style={{marginBottom: 12}} />
          <OutlineButton text={"button.cancel"} fullWidth={true} disabled={loading} click={() => setDeletionRequested(false)} />
        </> : <>
          {isSuperAdmin && <>
            <Row>
              <TextField type={'number'} onChange={updateX} value={x.toString()} label={"car-parking.xaxis"} disabled={loading} />
              <TextField type={'number'} onChange={updateY} value={y.toString()} label={"car-parking.yaxis"} disabled={loading} />
            </Row>
            <Row>
              <TextField type={'number'} onChange={updateWidth} value={width.toString()} label={"car-parking.width"} disabled={loading} />
              <TextField type={'number'} onChange={updateHeight} value={height.toString()} label={"car-parking.height"} disabled={loading} />
            </Row>
            <TextField type={'number'} onChange={updateRotation} value={rotation.toString()} label={"car-parking.rotation"} disabled={loading} />
          </>}
          <Switch style={{marginBottom: 32}} value={active} label={'Active'} onChange={(val: boolean) => updateActive(val)} />
          <PrimaryButton text={"button.save"} fullWidth={true} click={save} disabled={loading} style={{marginBottom: 12}} />
          {isSuperAdmin && <OutlineButton text={"button.delete"}
                                          fullWidth={true}
                                          disabled={loading}
                                          borderColour={Colours.red}
                                          click={() => setDeletionRequested(true)} />}
        </>}
      </Container>
    </Dialog>
  )
}

interface Props {
  payload: {
    parkingSpace: CarParkSpace;
  };
}

const Container = styled.div`
  .textField {
    margin-bottom: 12px;
    p {
      margin-bottom: 0;
    }
  }
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  .textField:first-child {
    margin-right: 8px;
  }
  .textField:last-child {
    margin-left: 8px;
  }
`
